import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTouchFriendlyUI } from '../utils/useTouchFriendlyUI';
import AllCategoriesPanel from './AllCategoriesPanel';
import MobileCategorySheet from './MobileCategorySheet';
import './BrowseDealsMenu.css';

const DIRECTORY_ITEMS = [
  { id: 'categories', label: 'Categories', action: 'categories' },
  { id: 'stores', label: 'Stores & Merchants', action: 'stores' },
];

const DISCOVER_ITEMS = [
  { id: 'all-deals', label: 'All Deals', action: 'all-deals' },
  { id: 'featured', label: 'Featured Deals', action: 'featured' },
  { id: 'new', label: 'New Deals', action: 'new' },
  { id: 'ending-soon', label: 'Ending Soon', action: 'ending-soon' },
];

const ROUTE_PREFETCH = {
  'all-deals': () => import('../pages/AllDealsPage'),
  new: () => import('../pages/NewDealsPage'),
  'ending-soon': () => import('../pages/EndingSoonPage'),
  stores: () => import('../pages/Outlets'),
};

function prefetchBrowseRoute(action) {
  ROUTE_PREFETCH[action]?.();
}

function usePrefersHoverMenu() {
  const [prefersHover, setPrefersHover] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => setPrefersHover(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return prefersHover;
}

const BrowseDealsMenu = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const categoriesCloseTimerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const prefersHoverMenu = usePrefersHoverMenu();
  const useTapMenus = useTouchFriendlyUI(768);
  const useCategorySheet = useTapMenus;

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setCategoriesOpen(false);
    setCategorySheetOpen(false);
    wrapRef.current?.querySelector('.browse-deals-btn')?.blur();
  }, []);

  const openMenu = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setMenuOpen(true);
  }, []);

  const cancelCategoriesClose = useCallback(() => {
    if (categoriesCloseTimerRef.current) {
      clearTimeout(categoriesCloseTimerRef.current);
      categoriesCloseTimerRef.current = null;
    }
  }, []);

  const scheduleCategoriesClose = useCallback(() => {
    if (!prefersHoverMenu) return;
    cancelCategoriesClose();
    categoriesCloseTimerRef.current = setTimeout(() => {
      categoriesCloseTimerRef.current = null;
      setCategoriesOpen(false);
    }, 120);
  }, [prefersHoverMenu, cancelCategoriesClose]);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, location.hash, closeMenu]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (categoriesCloseTimerRef.current) clearTimeout(categoriesCloseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen && !categoriesOpen) return undefined;
    const onPointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) closeMenu();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [menuOpen, categoriesOpen, closeMenu]);

  useEffect(() => {
    const header = wrapRef.current?.closest('.header');
    if (!header) return undefined;
    if (menuOpen) header.classList.add('header--browse-menu-open');
    else header.classList.remove('header--browse-menu-open');
    return () => header.classList.remove('header--browse-menu-open');
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      document.documentElement.style.removeProperty('--browse-deals-menu-top');
      return undefined;
    }
    const syncMenuTop = () => {
      const btn = wrapRef.current?.querySelector('.browse-deals-btn');
      if (!btn) return;
      const { bottom } = btn.getBoundingClientRect();
      document.documentElement.style.setProperty('--browse-deals-menu-top', `${bottom + 6}px`);
    };
    syncMenuTop();
    window.addEventListener('resize', syncMenuTop);
    window.addEventListener('scroll', syncMenuTop, { passive: true });
    return () => {
      window.removeEventListener('resize', syncMenuTop);
      window.removeEventListener('scroll', syncMenuTop);
      document.documentElement.style.removeProperty('--browse-deals-menu-top');
    };
  }, [menuOpen, categoriesOpen]);

  useEffect(() => {
    if (!menuOpen && !categoriesOpen && !categorySheetOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (categorySheetOpen) return;
      if (categoriesOpen) {
        setCategoriesOpen(false);
      } else {
        closeMenu();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, categoriesOpen, categorySheetOpen, closeMenu]);

  const scrollToSection = (sectionId) => {
    const run = () => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      setTimeout(run, 400);
    } else {
      window.history.replaceState({}, document.title, `/#${sectionId}`);
      setTimeout(run, 100);
    }
  };

  const handleItemClick = (action) => {
    if (action === 'categories') {
      if (useCategorySheet) {
        setCategorySheetOpen(true);
        setMenuOpen(false);
        setCategoriesOpen(false);
        return;
      }
      setMenuOpen(true);
      setCategoriesOpen((open) => !open);
      return;
    }
    closeMenu();
    switch (action) {
      case 'stores':
      case 'stores & merchants':
        navigate('/outlets');
        break;
      case 'all-deals':
        navigate('/deals');
        break;
      case 'featured':
        scrollToSection('weekly');
        break;
      case 'new':
        navigate('/new-deals');
        break;
      case 'ending-soon':
        navigate('/ending-soon');
        break;
      default:
        break;
    }
  };

  const handleCategoriesEnter = () => {
    cancelCategoriesClose();
    if (prefersHoverMenu && menuOpen) setCategoriesOpen(true);
  };

  const handleOtherItemEnter = () => {
    if (prefersHoverMenu) setCategoriesOpen(false);
  };

  const handleWrapMouseEnter = () => {
    if (!prefersHoverMenu) return;
    openMenu();
    prefetchBrowseRoute('new');
    prefetchBrowseRoute('ending-soon');
  };

  const handleWrapMouseLeave = () => {
    if (!prefersHoverMenu) return;
    closeTimerRef.current = setTimeout(() => closeMenu(), 120);
  };

  const handleButtonClick = () => {
    setMenuOpen((open) => !open);
    if (menuOpen) {
      setCategoriesOpen(false);
      setCategorySheetOpen(false);
    }
  };

  const renderMenuItem = (item) => {
    if (item.action === 'categories') {
      return (
        <li
          key={item.id}
          role="none"
          className={`browse-deals-dropdown-row browse-deals-dropdown-row--submenu${categoriesOpen && !useCategorySheet ? ' browse-deals-dropdown-row--subopen' : ''}`}
        >
          <div
            className="browse-deals-submenu-anchor"
            onMouseEnter={handleCategoriesEnter}
            onMouseLeave={scheduleCategoriesClose}
          >
            <button
              type="button"
              className="browse-deals-dropdown-item browse-deals-dropdown-item--submenu"
              role="menuitem"
              aria-expanded={useCategorySheet ? categorySheetOpen : categoriesOpen}
              aria-haspopup="true"
              onClick={() => handleItemClick(item.action)}
            >
              <span>{item.label}</span>
              <span className="browse-deals-submenu-chevron" aria-hidden>
                ›
              </span>
            </button>
            {categoriesOpen && !useCategorySheet ? (
              <AllCategoriesPanel onClose={() => setCategoriesOpen(false)} />
            ) : null}
          </div>
        </li>
      );
    }

    return (
      <li
        key={item.id}
        role="none"
        onMouseEnter={() => {
          handleOtherItemEnter();
          prefetchBrowseRoute(item.action);
        }}
      >
        <button
          type="button"
          className="browse-deals-dropdown-item"
          role="menuitem"
          onClick={() => handleItemClick(item.action)}
          onFocus={() => prefetchBrowseRoute(item.action)}
        >
          {item.label}
        </button>
      </li>
    );
  };

  return (
    <>
      <div
        className={`browse-deals-wrap${menuOpen ? ' browse-deals-wrap--open' : ''}${categoriesOpen ? ' browse-deals-wrap--categories-open' : ''}`}
        ref={wrapRef}
        onMouseEnter={handleWrapMouseEnter}
        onMouseLeave={handleWrapMouseLeave}
      >
        <button
          type="button"
          className="browse-deals-btn"
          onClick={handleButtonClick}
          aria-expanded={menuOpen}
          aria-haspopup="true"
          aria-label="Browse deals"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          <span>Browse Deals</span>
        </button>

        <ul className="browse-deals-dropdown" role="menu">
          <li role="presentation" className="browse-deals-dropdown-section">
            <span className="browse-deals-dropdown-heading">Directories</span>
            <ul className="browse-deals-dropdown-group" role="group" aria-label="Directories">
              {DIRECTORY_ITEMS.map((item) => renderMenuItem(item))}
            </ul>
          </li>

          <li role="separator" className="browse-deals-dropdown-divider browse-deals-discover-divider" aria-hidden="true" />

          <li role="presentation" className="browse-deals-dropdown-section browse-deals-discover-section">
            <span className="browse-deals-dropdown-heading">Discover</span>
            <ul className="browse-deals-dropdown-group" role="group" aria-label="Discover">
              {DISCOVER_ITEMS.map((item) => renderMenuItem(item))}
            </ul>
          </li>
        </ul>
      </div>

      {useCategorySheet ? (
        <MobileCategorySheet
          open={categorySheetOpen}
          onClose={() => setCategorySheetOpen(false)}
        />
      ) : null}
    </>
  );
};

export default BrowseDealsMenu;
